/**
 * Tiny CSV downloader shared by all admin tables.
 *
 * Usage:
 *   downloadCSV({
 *     filename: "guard_appeals.csv",
 *     columns: [
 *       { key: "user_name", header: "User" },
 *       { key: "user_email", header: "Email" },
 *       { key: "status", header: "Status" },
 *     ],
 *     data: appeals,
 *   });
 *
 * Each cell is RFC-4180 escaped: doubled quotes + wrap-in-quotes when the cell
 * contains commas, quotes, or newlines. Functions in `key` are supported and
 * receive the full row, useful for derived columns.
 *
 * Returns the row count so callers can show a toast ("Exported N rows").
 */
function escapeCell(val) {
  if (val === null || val === undefined) return "";
  let s = typeof val === "string" ? val : String(val);
  // Always strip CR (Windows clients add their own); LF inside quoted cells is fine
  s = s.replace(/\r/g, "");
  if (/[",\n]/.test(s)) {
    s = `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function downloadCSV({ filename, columns, data }) {
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeData = Array.isArray(data) ? data : [];

  const header = safeColumns.map((c) => escapeCell(c.header || c.key)).join(",");
  const lines = safeData.map((row) =>
    safeColumns
      .map((c) => {
        const raw = typeof c.key === "function" ? c.key(row) : row?.[c.key];
        return escapeCell(raw);
      })
      .join(","),
  );

  // Prepend BOM so Excel opens UTF-8 (Naira ₦, accents, etc.) correctly
  const csv = "\ufeff" + [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "export.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return safeData.length;
}

/**
 * Helper that prepends today's ISO date to a base filename.
 * stampedFilename("guard_appeals") -> "guard_appeals_2026-04-28.csv"
 */
export function stampedFilename(base) {
  const now = new Date();
  const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `${base}_${stamp}.csv`;
}
